import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Loader2, Users, TrendingUp, DollarSign, Activity,
  Sparkles, Crown, AlertCircle, BarChart3, Calendar,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import AdminCompaniesTable from "../components/AdminCompaniesTable";
import AdminResetLinkGenerator from "../components/AdminResetLinkGenerator";
import AdminAuditLog from "../components/AdminAuditLog";
import AdminOcrStats from "../components/AdminOcrStats";

const FMT_EUR = new Intl.NumberFormat("ca-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const FMT_NUM = new Intl.NumberFormat("ca-ES");

export default function AdminDashboard() {
  const { lang } = useI18n();
  const [authz, setAuthz] = useState({ checking: true, allowed: false });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/admin/me");
        if (!me.data.is_superadmin) {
          setAuthz({ checking: false, allowed: false });
          return;
        }
        setAuthz({ checking: false, allowed: true });
        const r = await api.get("/admin/saas-stats");
        setStats(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Error");
        setAuthz({ checking: false, allowed: false });
      }
    })();
  }, []);

  if (authz.checking) {
    return (
      <div className="card-surface p-16 text-center text-slate-400">
        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
      </div>
    );
  }
  if (!authz.allowed) {
    return <Navigate to="/app" replace />;
  }
  if (!stats) {
    return (
      <div className="card-surface p-16 text-center text-slate-400">
        {error || (
          <>
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
            {lang === "es" ? "Cargando estadísticas..." : "Carregant estadístiques..."}
          </>
        )}
      </div>
    );
  }

  const t = stats.totals;
  const b = stats.buckets;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-[0_0_24px_rgba(168,85,247,0.45)]">
          <Crown className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-white">
            {lang === "es" ? "Panel super-admin HOSYS" : "Panell super-admin HOSYS"}
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {lang === "es"
              ? "Telemetría SaaS · Solo visible para propietarios"
              : "Telemetria SaaS · Només visible per als propietaris"}
            <span className="opacity-50"> · {new Date(stats.generated_at).toLocaleString()}</span>
          </p>
        </div>
      </div>

      {/* Hero KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiHero
          icon={DollarSign}
          label="MRR"
          value={FMT_EUR.format(t.mrr_eur)}
          subtitle={`ARR ${FMT_EUR.format(t.annual_run_rate_eur)}`}
          accent="emerald"
          testid="kpi-mrr"
        />
        <KpiHero
          icon={Users}
          label={lang === "es" ? "Empresas totales" : "Empreses totals"}
          value={FMT_NUM.format(t.companies)}
          subtitle={`${b.active + b.trialing} ${lang === "es" ? "activas" : "actives"}`}
          accent="blue"
          testid="kpi-companies"
        />
        <KpiHero
          icon={TrendingUp}
          label={lang === "es" ? "Conversión" : "Conversió"}
          value={`${Math.round(t.conversion_rate * 100)}%`}
          subtitle={lang === "es" ? "Pagados / funnel" : "Pagats / funnel"}
          accent="purple"
          testid="kpi-conversion"
        />
        <KpiHero
          icon={Activity}
          label={lang === "es" ? "Factures 30d" : "Factures 30d"}
          value={FMT_NUM.format(t.invoices_issued_30d + t.invoices_received_30d)}
          subtitle={`${t.invoices_issued_30d} ${lang === "es" ? "emit." : "emes."} · ${t.invoices_received_30d} ${lang === "es" ? "rec." : "reb."}`}
          accent="indigo"
          testid="kpi-activity"
        />
      </div>

      {/* Bucket breakdown */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={14} className="text-slate-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {lang === "es" ? "Distribución de empresas" : "Distribució d'empreses"}
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <BucketCell label={lang === "es" ? "Sin suscribir" : "Sense subscriure"}
                      count={b.none} accent="slate" />
          <BucketCell label="Trial" count={b.trialing} accent="purple" />
          <BucketCell label={lang === "es" ? "Activas pago" : "Actives pago"}
                      count={b.active} accent="emerald" />
          <BucketCell label={lang === "es" ? "Expiradas" : "Expirades"}
                      count={b.expired} accent="rose" />
          <BucketCell label={lang === "es" ? "Propietat" : "Propietat"}
                      count={b.owner} accent="amber" icon={Crown} />
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            {lang === "es" ? "Planes de pago activos" : "Plans de pagament actius"}
          </div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(stats.plan_counts).map(([plan, count]) => (
              <PlanCounter key={plan} plan={plan} count={count} />
            ))}
          </div>
        </div>
      </div>

      {/* Two columns: trialing + paid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CompaniesPanel
          title={lang === "es" ? "En periodo de prueba" : "En període de prova"}
          icon={Sparkles}
          accent="purple"
          companies={stats.trialing_companies}
          empty={lang === "es" ? "Sin trials activos" : "Sense trials actius"}
          extra={(c) => (
            <span className="text-[10px] text-purple-300 font-mono">
              {Math.max(0, Math.floor(c.days_remaining))}d
            </span>
          )}
          testid="panel-trialing"
        />
        <CompaniesPanel
          title={lang === "es" ? "Suscripciones pagadas" : "Subscripcions pagades"}
          icon={DollarSign}
          accent="emerald"
          companies={stats.paid_companies}
          empty={lang === "es" ? "Sin suscripciones aún" : "Sense subscripcions encara"}
          extra={(c) => (
            <span className="text-[10px] text-emerald-300 capitalize font-mono">
              {c.plan} · {Math.floor(c.days_remaining)}d
            </span>
          )}
          testid="panel-paid"
        />
      </div>

      {/* Top companies */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={14} className="text-slate-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {lang === "es" ? "Top 5 empresas (últimos 30 días)" : "Top 5 empreses (últims 30 dies)"}
          </h2>
        </div>
        {stats.top_companies_30d.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            {lang === "es" ? "Sin actividad" : "Sense activitat"}
          </p>
        ) : (
          <div className="space-y-2">
            {stats.top_companies_30d.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-[10px] font-bold text-indigo-200">
                    {i + 1}
                  </div>
                  <span className="text-sm text-slate-200 truncate">{c.name}</span>
                </div>
                <span className="text-xs font-mono text-slate-400 flex-shrink-0">
                  {c.issued_count} {lang === "es" ? "fac." : "fac."}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OCR cost telemetry (Tesseract vs GPT-4o) */}
      <AdminOcrStats />

      {/* Manual company management (FASE 8.1) */}
      <AdminCompaniesTable />

      {/* Manual reset link generator (support tool) */}
      <AdminResetLinkGenerator />

      {/* Super-admin action audit log */}
      <AdminAuditLog />
    </div>
  );
}

function KpiHero({ icon: Icon, label, value, subtitle, accent, testid }) {
  const palette = {
    emerald: "from-emerald-500/15 to-emerald-600/5 border-emerald-500/30 text-emerald-300",
    blue: "from-blue-500/15 to-blue-600/5 border-blue-500/25 text-blue-300",
    purple: "from-purple-500/15 to-purple-600/5 border-purple-500/25 text-purple-300",
    indigo: "from-indigo-500/15 to-indigo-600/5 border-indigo-500/25 text-indigo-300",
  }[accent];
  return (
    <div className={`card-surface bg-gradient-to-br ${palette} border p-4 relative overflow-hidden`}
         data-testid={testid}>
      <Icon className="absolute -right-3 -top-3 w-16 h-16 opacity-10" />
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </div>
      <div className="text-3xl font-bold text-white tabular-nums">{value}</div>
      {subtitle && (
        <div className="text-[11px] text-slate-400 mt-1.5">{subtitle}</div>
      )}
    </div>
  );
}

function BucketCell({ label, count, accent, icon: Icon }) {
  const palette = {
    slate: "bg-white/5 text-slate-300 border-white/10",
    purple: "bg-purple-500/10 text-purple-200 border-purple-500/30",
    emerald: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
    rose: "bg-rose-500/10 text-rose-200 border-rose-500/30",
    amber: "bg-amber-500/10 text-amber-100 border-amber-500/40",
  }[accent];
  return (
    <div className={`rounded-lg border ${palette} p-3 flex flex-col items-start gap-1`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {Icon ? <Icon size={10} /> : null}
        {label}
      </div>
      <div className="text-2xl font-bold tabular-nums">{count}</div>
    </div>
  );
}

function PlanCounter({ plan, count }) {
  const palette = {
    basic: "bg-blue-500/10 text-blue-200 border-blue-500/30",
    pro: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
    enterprise: "bg-purple-500/10 text-purple-200 border-purple-500/30",
  }[plan] || "bg-white/5 text-slate-300 border-white/10";
  return (
    <div className={`px-3 py-1.5 rounded-full border text-xs ${palette}`}>
      <span className="capitalize font-semibold">{plan}</span>
      <span className="opacity-70 font-mono ml-2">{count}</span>
    </div>
  );
}

function CompaniesPanel({ title, icon: Icon, accent, companies, empty, extra, testid }) {
  const headerColor = {
    purple: "text-purple-300",
    emerald: "text-emerald-300",
  }[accent] || "text-slate-300";
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={headerColor} />
        <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          {title}
        </h2>
        <span className="ml-auto text-xs text-slate-500 font-mono">
          {companies.length}
        </span>
      </div>
      {companies.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">{empty}</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {companies.map((c) => (
            <div key={c.id}
                 className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-white/[0.03]">
              <span className="text-sm text-slate-200 truncate">{c.name}</span>
              {extra(c)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
